import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarCheck, FaCalendar, FaEdit, FaUsers, FaComments, FaExternalLinkAlt, FaPlus, FaCommentDots, FaSlidersH } from "react-icons/fa";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Collapse from 'react-bootstrap/Collapse';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import httpService from '../../services/httpService';
import '../../styles/PodcasterEpisodeItem.scss';

const PodcasterEpisodeItem = ({ episode, clubGuid, onEditEpisode, onViewUsers }) => {
    const navigate = useNavigate();

    const [showDiscussionsModal, setShowDiscussionsModal] = useState(false);
    const [discussions, setDiscussions] = useState([]);
    const [loadingDiscussions, setLoadingDiscussions] = useState(false);
    const [discussionError, setDiscussionError] = useState(null);

    const [showNewDiscussion, setShowNewDiscussion] = useState(false);
    const [newDiscussion, setNewDiscussion] = useState({ title: '', body: '', is_pinned: 0, status: 'hidden', allow_comments: true });
    const [submittingDiscussion, setSubmittingDiscussion] = useState(false);
    const [newDiscussionErrors, setNewDiscussionErrors] = useState({});

    const [cardOpen, setCardOpen] = useState({});
    const [cardSettings, setCardSettings] = useState({});
    const [cardSaving, setCardSaving] = useState({});
    const [cardStats, setCardStats] = useState({});

    useEffect(() => {
        console.log('PodcasterEpisodeItem props:', { episode });
    }, []);

    const handleEditEpisode = () => {
        console.log('Opening edit modal for episode:', episode.id);
        onEditEpisode(episode);
    };

    const handleViewUsers = () => {
        console.log('Opening users data modal for episode:', episode.id);
        onViewUsers(episode);
    };

    const openDiscussionsModal = () => {
        setShowDiscussionsModal(true);
        setLoadingDiscussions(true);
        setDiscussionError(null);
        httpService.get(`/dash/club/${clubGuid}/episode/${episode.pivot.guid}/discussions`)
            .then((res) => setDiscussions(res.data.data || []))
            .catch((err) => setDiscussionError(err.response?.data?.message || 'Error loading discussions'))
            .finally(() => setLoadingDiscussions(false));
    };

    const handleCreateDiscussion = async () => {
        setSubmittingDiscussion(true);
        setNewDiscussionErrors({});
        try {
            await httpService.post(`/dash/club/${clubGuid}/discussions`, {
                ...newDiscussion,
                episode_pivot_id: episode.pivot.id,
            });
            setNewDiscussion({ title: '', body: '', is_pinned: 0, status: 'hidden', allow_comments: true });
            setShowNewDiscussion(false);
            openDiscussionsModal();
        } catch (err) {
            setNewDiscussionErrors(err.response?.data?.errors || {});
        } finally {
            setSubmittingDiscussion(false);
        }
    };

    const fetchCardStats = async (guid) => {
        setCardStats(prev => ({ ...prev, [guid]: { loading: true } }));
        try {
            const res = await httpService.get(`/dash/club/${clubGuid}/discussions/${guid}?pageSize=50`);
            const comments = res.data.data?.comments || [];

            const userCounts = {};
            comments.forEach(c => {
                if (c.user?.id) {
                    userCounts[c.user.id] = { count: (userCounts[c.user.id]?.count || 0) + 1, user: c.user };
                }
            });
            const mostActiveUser = Object.values(userCounts).sort((a, b) => b.count - a.count)[0]?.user || null;

            const mostLikedComment = comments.reduce((best, c) => {
                const score = (c.reactions_like || 0) + (c.reactions_love || 0);
                const bestScore = (best?.reactions_like || 0) + (best?.reactions_love || 0);
                return score > bestScore ? c : best;
            }, null);

            setCardStats(prev => ({ ...prev, [guid]: { loading: false, mostActiveUser, mostLikedComment } }));
        } catch {
            setCardStats(prev => ({ ...prev, [guid]: { loading: false } }));
        }
    };

    const handleToggleCard = (disc) => {
        const isOpening = !cardOpen[disc.guid];
        setCardOpen(prev => ({ ...prev, [disc.guid]: isOpening }));
        if (isOpening) {
            if (!cardSettings[disc.guid]) {
                setCardSettings(prev => ({
                    ...prev,
                    [disc.guid]: { status: disc.status, is_pinned: disc.is_pinned, allow_comments: disc.allow_comments },
                }));
            }
            if (!cardStats[disc.guid]) {
                fetchCardStats(disc.guid);
            }
        }
    };

    const handleSaveCardSettings = async (disc) => {
        setCardSaving(prev => ({ ...prev, [disc.guid]: true }));
        try {
            await httpService.put(`/dash/club/${clubGuid}/discussions/${disc.guid}`, {
                title: disc.title,
                content: disc.body,
                ...cardSettings[disc.guid],
            });
            setDiscussions(prev => prev.map(d =>
                d.guid === disc.guid ? { ...d, ...cardSettings[disc.guid] } : d
            ));
        } catch {
            // httpService surfaces the error as a toast automatically
        } finally {
            setCardSaving(prev => ({ ...prev, [disc.guid]: false }));
        }
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return 'Not set';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const isEpisodeActive = (startDate, endDate) => {
        if (!startDate || !endDate) return false;
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);
        return now >= start && now <= end;
    };

    const getActiveStatusTooltip = (startDate, endDate) => {
        if (!startDate || !endDate) return 'Episode dates not set';

        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        const formatLongDate = (date) => {
            return date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        };

        if (now < start) {
            // Future episode
            return `This episode will be active starting ${formatLongDate(start)} and will end on ${formatLongDate(end)}.`;
        } else if (now > end) {
            // Past episode
            return `This episode was active from ${formatLongDate(start)} to ${formatLongDate(end)}.`;
        } else {
            // Currently active
            return `This episode is currently active! It started on ${formatLongDate(start)} and will end on ${formatLongDate(end)}.`;
        }
    };

    const isActive = isEpisodeActive(episode.pivot.start_date_time, episode.pivot.end_date_time);
    const tooltipMessage = getActiveStatusTooltip(episode.pivot.start_date_time, episode.pivot.end_date_time);

    return (
        <div style={{ borderLeft: '10px solid ' + episode.pivot.color, width: '100%' }} className={"mb-2 p-2 border-bottom component-PodcasterEpisodeItem"}>
            <div className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold" style={{ width: '300px', flexShrink: 0 }}>
                    {episode.title}
                </span>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">
                        {episode.pivot.start_date_time && new Date(episode.pivot.start_date_time) > new Date()
                            ? `Episode to be released on: ${formatShortDate(episode.pivot.start_date_time)}`
                            : `Episode released on: ${formatShortDate(episode.pivot.start_date_time)}`
                        }
                    </span>
                    {/*<OverlayTrigger*/}
                    {/*    placement="top"*/}
                    {/*    overlay={*/}
                    {/*        <Tooltip id={`tooltip-${episode.id}`}>*/}
                    {/*            {tooltipMessage}*/}
                    {/*        </Tooltip>*/}
                    {/*    }*/}
                    {/*>*/}
                    {/*    <span style={{ cursor: 'pointer' }}>*/}
                    {/*        {isActive ? (*/}
                    {/*            <FaCalendarCheck style={{ color: '#7d7', fontSize: '18px' }} />*/}
                    {/*        ) : (*/}
                    {/*            <FaCalendar style={{ color: "grey", fontSize: '18px' }} />*/}
                    {/*        )}*/}
                    {/*    </span>*/}
                    {/*</OverlayTrigger>*/}
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-edit-${episode.id}`}>Edit Episode</Tooltip>}>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleEditEpisode}
                        >
                            <FaEdit />
                        </Button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-users-${episode.id}`}>View users statistics</Tooltip>}>
                        <Button
                            variant="outline-info"
                            size="sm"
                            onClick={handleViewUsers}
                        >
                            <FaUsers />
                        </Button>
                    </OverlayTrigger>
                    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-discussions-${episode.id}`}>View Discussions</Tooltip>}>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={openDiscussionsModal}
                        >
                            <FaComments />
                        </Button>
                    </OverlayTrigger>
                </div>
            </div>

            <Modal show={showDiscussionsModal} onHide={() => setShowDiscussionsModal(false)} size="lg" className="pei-modal">
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>
                            <FaCommentDots className="me-2" style={{ opacity: 0.7 }} />
                            Discussions
                        </Modal.Title>
                        <div className="pei-modal__episode-sub">{episode.title}</div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    {/* ── New discussion ── */}
                    <div className="mb-4">
                        <Button
                            variant={showNewDiscussion ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="pei-modal__new-btn"
                            onClick={() => setShowNewDiscussion(prev => !prev)}
                            aria-controls="new-discussion-collapse"
                            aria-expanded={showNewDiscussion}
                        >
                            <FaPlus /> New Discussion
                        </Button>

                        <Collapse in={showNewDiscussion}>
                            <div id="new-discussion-collapse">
                                <div className="pei-modal__form-panel">
                                    <div className="pei-modal__form-panel-header">New Discussion</div>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Title</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Give this discussion a clear title…"
                                            value={newDiscussion.title}
                                            onChange={(e) => setNewDiscussion(prev => ({ ...prev, title: e.target.value }))}
                                            isInvalid={!!newDiscussionErrors.title}
                                            disabled={submittingDiscussion}
                                        />
                                        <Form.Control.Feedback type="invalid">{newDiscussionErrors.title}</Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-semibold">Body</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            placeholder="What would you like to discuss?"
                                            value={newDiscussion.body}
                                            onChange={(e) => setNewDiscussion(prev => ({ ...prev, body: e.target.value }))}
                                            isInvalid={!!newDiscussionErrors.body}
                                            disabled={submittingDiscussion}
                                        />
                                        <Form.Control.Feedback type="invalid">{newDiscussionErrors.body}</Form.Control.Feedback>
                                    </Form.Group>

                                    <Row className="g-3 align-items-end mb-3">
                                        <Col xs="auto">
                                            <Form.Group>
                                                <Form.Label className="small fw-semibold">Status</Form.Label>
                                                <Form.Select
                                                    size="sm"
                                                    value={newDiscussion.status}
                                                    onChange={(e) => setNewDiscussion(prev => ({ ...prev, status: e.target.value }))}
                                                    disabled={submittingDiscussion}
                                                >
                                                    <option value="hidden">Hidden</option>
                                                    <option value="published">Published</option>
                                                    <option value="archived">Archived</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col xs="auto">
                                            <div className="pei-modal__toggles">
                                                <Form.Check
                                                    type="switch"
                                                    id={`pinned-switch-${episode.id}`}
                                                    label="Pinned"
                                                    checked={newDiscussion.is_pinned === 1}
                                                    onChange={(e) => setNewDiscussion(prev => ({ ...prev, is_pinned: e.target.checked ? 1 : 0 }))}
                                                    disabled={submittingDiscussion}
                                                />
                                                <Form.Check
                                                    type="switch"
                                                    id={`comments-switch-${episode.id}`}
                                                    label="Allow Comments"
                                                    checked={newDiscussion.allow_comments}
                                                    onChange={(e) => setNewDiscussion(prev => ({ ...prev, allow_comments: e.target.checked }))}
                                                    disabled={submittingDiscussion}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="d-flex gap-2 justify-content-end">
                                        <Button size="sm" variant="light" onClick={() => setShowNewDiscussion(false)} disabled={submittingDiscussion}>
                                            Cancel
                                        </Button>
                                        <Button size="sm" variant="primary" onClick={handleCreateDiscussion} disabled={submittingDiscussion}>
                                            {submittingDiscussion
                                                ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Creating…</>
                                                : <><FaPlus className="me-1" />Create Discussion</>
                                            }
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>

                    {/* ── Discussion list ── */}
                    {loadingDiscussions && (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}

                    {discussionError && (
                        <div className="alert alert-danger py-2">{discussionError}</div>
                    )}

                    {!loadingDiscussions && !discussionError && discussions.length === 0 && (
                        <div className="pei-modal__empty">
                            <FaCommentDots size={28} className="mb-2 d-block mx-auto" style={{ opacity: 0.25 }} />
                            No discussions yet for this episode.
                        </div>
                    )}

                    {!loadingDiscussions && discussions.length > 0 && (
                        <div className="pei-modal__list">
                            {discussions.map((disc) => (
                                <div key={disc.guid} className={`pei-modal__disc-card pei-modal__disc-card--${disc.status}`}>
                                    <div className="pei-modal__disc-card__top">
                                        <div className="pei-modal__disc-card__body">
                                            <div className="pei-modal__disc-card__title-row">
                                                {disc.is_pinned === 1 && <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>Pinned</Badge>}
                                                <Badge
                                                    bg={disc.status === 'published' ? 'success' : disc.status === 'archived' ? 'warning' : 'secondary'}
                                                    style={{ fontSize: '0.65rem' }}
                                                >
                                                    {disc.status}
                                                </Badge>
                                                <span className="pei-modal__disc-card__title">{disc.title}</span>
                                            </div>
                                            <div className="pei-modal__disc-card__meta">
                                                <FaComments className="me-1" style={{ opacity: 0.5 }} />
                                                {disc.comments_count} comment{disc.comments_count !== 1 ? 's' : ''}
                                                {disc.last_comment_at && (
                                                    <> · last activity {new Date(disc.last_comment_at).toLocaleDateString()}</>
                                                )}
                                            </div>
                                        </div>
                                        <OverlayTrigger placement="left" overlay={<Tooltip id={`tooltip-manage-${disc.guid}`}>Manage</Tooltip>}>
                                            <Button
                                                variant={cardOpen[disc.guid] ? 'secondary' : 'outline-secondary'}
                                                size="sm"
                                                onClick={() => handleToggleCard(disc)}
                                                aria-controls={`card-collapse-${disc.guid}`}
                                                aria-expanded={!!cardOpen[disc.guid]}
                                            >
                                                <FaSlidersH />
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="left" overlay={<Tooltip id={`tooltip-view-disc-${disc.guid}`}>Open Discussion</Tooltip>}>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => navigate(`/podcaster/podcast-club/${clubGuid}/discussion/${disc.guid}`)}
                                            >
                                                <FaExternalLinkAlt />
                                            </Button>
                                        </OverlayTrigger>
                                    </div>

                                    <Collapse in={!!cardOpen[disc.guid]}>
                                        <div id={`card-collapse-${disc.guid}`} className="pei-modal__disc-card__panels">
                                            <Row className="g-2 align-items-end justify-content-start pt-3">
                                                <Col xs="auto" className="text-center">
                                                    <Form.Select
                                                        size="sm"
                                                        value={cardSettings[disc.guid]?.status || disc.status}
                                                        onChange={(e) => setCardSettings(prev => ({
                                                            ...prev,
                                                            [disc.guid]: { ...prev[disc.guid], status: e.target.value },
                                                        }))}
                                                    >
                                                        <option value="published">Published</option>
                                                        <option value="archived">Archived</option>
                                                        <option value="hidden">Hidden</option>
                                                    </Form.Select>
                                                </Col>
                                                <Col xs="auto" className="text-center">
                                                    <Form.Check
                                                        type="switch"
                                                        id={`pinned-${disc.guid}`}
                                                        label="Pinned"
                                                        checked={!!(cardSettings[disc.guid]?.is_pinned ?? disc.is_pinned)}
                                                        onChange={(e) => setCardSettings(prev => ({
                                                            ...prev,
                                                            [disc.guid]: { ...prev[disc.guid], is_pinned: e.target.checked ? 1 : 0 },
                                                        }))}
                                                    />
                                                </Col>
                                                <Col xs="auto" className="text-center">
                                                    <Form.Check
                                                        type="switch"
                                                        id={`allow-comments-${disc.guid}`}
                                                        label="Allow Comments"
                                                        checked={!!(cardSettings[disc.guid]?.allow_comments ?? disc.allow_comments)}
                                                        onChange={(e) => setCardSettings(prev => ({
                                                            ...prev,
                                                            [disc.guid]: { ...prev[disc.guid], allow_comments: e.target.checked },
                                                        }))}
                                                    />
                                                </Col>
                                                <Col xs="auto" className="text-center">
                                                    <Button
                                                        size="sm"
                                                        variant="primary"
                                                        disabled={!!cardSaving[disc.guid]}
                                                        onClick={() => handleSaveCardSettings(disc)}
                                                    >
                                                        {cardSaving[disc.guid]
                                                            ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Saving…</>
                                                            : 'Save'
                                                        }
                                                    </Button>
                                                </Col>
                                            </Row>

                                            {/*<div className="pei-modal__disc-card__stats">*/}
                                            {/*    {cardStats[disc.guid]?.loading ? (*/}
                                            {/*        <Spinner animation="border" size="sm" />*/}
                                            {/*    ) : (*/}
                                            {/*        <>*/}
                                            {/*            <span><strong>{disc.comments_count}</strong> total comments</span>*/}
                                            {/*            {cardStats[disc.guid]?.mostActiveUser && (*/}
                                            {/*                <span>*/}
                                            {/*                    Most active: <strong>*/}
                                            {/*                        {cardStats[disc.guid].mostActiveUser.first_name} {cardStats[disc.guid].mostActiveUser.last_name}*/}
                                            {/*                    </strong>*/}
                                            {/*                </span>*/}
                                            {/*            )}*/}
                                            {/*            {cardStats[disc.guid]?.mostLikedComment && (*/}
                                            {/*                <span>*/}
                                            {/*                    Top comment: "{cardStats[disc.guid].mostLikedComment.body?.slice(0, 50)}{cardStats[disc.guid].mostLikedComment.body?.length > 50 ? '…' : ''}"*/}
                                            {/*                </span>*/}
                                            {/*            )}*/}
                                            {/*        </>*/}
                                            {/*    )}*/}
                                            {/*</div>*/}
                                        </div>
                                    </Collapse>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowDiscussionsModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PodcasterEpisodeItem;